import React, { useState, useEffect } from 'react';
import { 
  getMessagesForUser, 
  getManagerInbox, 
  sendMessage, 
  markMessageRead, 
  Message 
} from '../services/messageService';
import { getStaff, Staff } from '../services/staffService';
import { getUsersByTenant, User as ServiceUser } from '../services/userService';
import { useFirebase } from '../contexts/FirebaseContext';
import { isManagerialRole } from '../constants';
import { 
  Mail, 
  Send, 
  User as UserIcon, 
  Clock, 
  MessageSquare, 
  Plus, 
  Megaphone, 
  HelpCircle,
  CheckCircle,
  Archive
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface Props {
  tenantId: string;
}

export const CommunicationHub: React.FC<Props> = ({ tenantId }) => {
  const { user } = useFirebase();
  const [messages, setMessages] = useState<Message[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<'All' | 'Received' | 'Sent' | 'Announcements'>('All');
  
  // New Message Form State
  const [newMessage, setNewMessage] = useState({
    recipientUid: '',
    subject: '',
    body: '',
    type: 'Inquiry' as Message['type']
  });

  const [currentUserStaff, setCurrentUserStaff] = useState<Staff | null>(null);
  const [userMap, setUserMap] = useState<Record<string, string>>({});
  const [idToUid, setIdToUid] = useState<Record<string, string>>({});
  
  const isManager = (currentUserStaff && (isManagerialRole(currentUserStaff.role) || ['Manager', 'Salon Manager', 'Admin'].includes(currentUserStaff.role))) || user?.email === 'admin@growezy.ai';

  useEffect(() => {
    const init = async () => {
      if (!user) return;
      setLoading(true);
      const [staff, users] = await Promise.all([getStaff(tenantId), getUsersByTenant(tenantId)]);
      
      const emailToUid = users.reduce((acc, u) => ({ ...acc, [u.email]: u.uid }), {} as Record<string, string>);
      setUserMap(emailToUid);
      
      // Map BOTH staff and users to UIDs
      const idToUid = {
        ...staff.reduce((acc, s) => {
          const uid = emailToUid[s.email];
          return uid ? { ...acc, [s.id!]: uid } : acc;
        }, {} as Record<string, string>),
        ...users.reduce((acc, u) => ({ ...acc, [u.uid]: u.uid }), {} as Record<string, string>)
      };
      setIdToUid(idToUid);
      
      console.log('DEBUG: Staff list for ', tenantId, ':', staff);
      console.log('DEBUG: User map:', emailToUid);
      console.log('DEBUG: Id to Uid map:', idToUid);
      setStaffList(staff);
      
      const me = staff.find(s => s.email === user.email);
      console.log('DEBUG: Found current user in staff list:', me);
      
      // Ensure admin has a staff entry
      if (!me && user.email === 'admin@growezy.ai') {
        setCurrentUserStaff({
          id: user.uid,
          name: 'Platform Admin',
          role: 'Admin',
          email: user.email,
          tenantId: tenantId
        } as Staff);
      } else if (me) {
        setCurrentUserStaff(me);
      } else {
        console.warn('DEBUG: Current user not found in staff list!');
        setCurrentUserStaff(null);
      }

      const data = isManager ? await getManagerInbox(tenantId) : await getMessagesForUser(tenantId, user.uid);
      setMessages(data);
      setLoading(false);
    };
    init();
  }, [tenantId, user]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !currentUserStaff) return;

    try {
      await sendMessage(tenantId, {
        tenantId,
        senderUid: user.uid,
        senderName: currentUserStaff.name,
        senderRole: currentUserStaff.role,
        recipientUid: newMessage.recipientUid === 'broadcast' ? null : 
          (isManager ? 
            (idToUid[newMessage.recipientUid] || newMessage.recipientUid) : 
            (staffList.filter(s => isManagerialRole(s.role) || ['Manager', 'Admin', 'Salon Manager', 'Owner'].includes(s.role))
              .map(s => idToUid[s.id!])
              .find(uid => uid) || null) // Find first available manager UID or null
          ),
        subject: newMessage.subject,
        body: newMessage.body,
        type: newMessage.type,
        status: 'Unread'
      });

      // Refresh
      const data = isManager ? await getManagerInbox(tenantId) : await getMessagesForUser(tenantId, user.uid);
      setMessages(data);
      setIsNewMessageOpen(false);
      setNewMessage({ recipientUid: '', subject: '', body: '', type: 'Inquiry' });
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  const filteredMessages = messages.filter(m => {
    console.log('DEBUG: Filter Check:', { activeFilter, m, userUid: user?.uid });
    if (activeFilter === 'All') return true;
    if (activeFilter === 'Received') return m.recipientUid === user?.uid || m.recipientUid === null;
    if (activeFilter === 'Sent') return m.senderUid === user?.uid;
    if (activeFilter === 'Announcements') return m.type === 'Announcement';
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase italic">Communication Center</h1>
          <p className="text-gray-500 font-medium">Direct line between employees and management.</p>
        </div>
        <button 
          onClick={() => setIsNewMessageOpen(true)}
          className="btn-primary flex items-center gap-2 self-start md:self-center"
        >
          <Plus size={18} />
          {isManager ? 'New Announcement / Message' : 'Send Feedback / Inquiry'}
        </button>
      </div>

      <div className="flex items-center gap-4 border-b border-gray-100 pb-1 overflow-x-auto no-scrollbar">
        {['All', 'Received', 'Sent', 'Announcements'].map((f) => (
          <button
            key={f}
            onClick={() => setActiveFilter(f as any)}
            className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${
              activeFilter === f 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Message List */}
        <div className="lg:col-span-2 space-y-4">
          {loading ? (
            <div className="p-12 text-center text-gray-400">Loading messages...</div>
          ) : filteredMessages.length === 0 ? (
            <div className="card p-12 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
                <Mail size={32} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">No messages found</h3>
              <p className="text-gray-500 text-sm max-w-xs mx-auto">Start a conversation with management or wait for announcements.</p>
            </div>
          ) : (
            filteredMessages.map((m) => (
              <motion.div 
                layout
                key={m.id} 
                className={`card p-6 border-l-4 transition-all hover:shadow-md ${
                  m.status === 'Unread' && m.recipientUid === user?.uid 
                    ? 'border-l-blue-600 bg-blue-50/10' 
                    : m.type === 'Announcement'
                    ? 'border-l-amber-500 bg-amber-50/10'
                    : 'border-l-gray-200'
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      m.type === 'Announcement' ? 'bg-amber-100 text-amber-600' :
                      m.type === 'Feedback' ? 'bg-emerald-100 text-emerald-600' :
                      'bg-blue-100 text-blue-600'
                    }`}>
                      {m.type === 'Announcement' ? <Megaphone size={16} /> :
                       m.type === 'Help' ? <HelpCircle size={16} /> :
                       <MessageSquare size={16} />}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 leading-none mb-1">{m.subject}</h3>
                      <p className="text-[10px] text-gray-500 font-medium">
                        From: <span className="font-bold text-gray-700">{m.senderName}</span> ({m.senderRole}) • {m.timestamp?.toDate().toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {m.status === 'Unread' && m.recipientUid === user?.uid && (
                    <span className="px-2 py-0.5 bg-blue-600 text-white text-[8px] font-black rounded uppercase tracking-widest">New</span>
                  )}
                </div>
                <div className="text-sm text-gray-600 leading-relaxed bg-white/50 p-3 rounded-lg border border-gray-100 mb-4">
                  {m.body}
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border ${
                      m.type === 'Announcement' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                      m.type === 'Feedback' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                      'bg-blue-50 text-blue-700 border-blue-100'
                    }`}>
                      {m.type}
                    </span>
                  </div>
                  {m.recipientUid === user?.uid && m.status === 'Unread' && (
                    <button 
                      onClick={async () => {
                        await markMessageRead(tenantId, m.id!);
                        // Refresh
                        const data = isManager ? await getManagerInbox(tenantId) : await getMessagesForUser(tenantId, user!.uid);
                        setMessages(data);
                      }}
                      className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline"
                    >
                      Mark as Read
                    </button>
                  )}
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Info / Quick Links */}
        <div className="space-y-6">
          <div className="card p-6 bg-gray-900 text-white border-none shadow-xl">
            <h3 className="text-xl font-black italic tracking-tighter uppercase mb-4">Direct Communication</h3>
            <p className="text-sm text-gray-400 font-medium leading-relaxed mb-6">
              Use this portal to send questions about payroll, provide workplace feedback, or report system issues directly to your manager.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  <CheckCircle size={16} className="text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs font-bold">Secure & Private</p>
                  <p className="text-[10px] text-gray-500 font-medium">Locked to your business instance.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                  <Clock size={16} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-xs font-bold">Manager Notifications</p>
                  <p className="text-[10px] text-gray-500 font-medium">Managers are alerted to new feedback.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">System Activity</h4>
            <div className="space-y-4">
               <div className="flex gap-3">
                 <div className="w-1 h-10 bg-blue-100 rounded-full" />
                 <div>
                   <p className="text-xs font-bold text-gray-900">Announcement Broadcast</p>
                   <p className="text-[10px] text-gray-500 font-medium">Managers can send messages to all employees at once.</p>
                 </div>
               </div>
               <div className="flex gap-3">
                 <div className="w-1 h-10 bg-emerald-100 rounded-full" />
                 <div>
                   <p className="text-xs font-bold text-gray-900">Feedback Loop</p>
                   <p className="text-[10px] text-gray-500 font-medium">Continuous improvement through direct team submittals.</p>
                 </div>
               </div>
            </div>
          </div>
        </div>
      </div>

      {/* New Message Modal */}
      <AnimatePresence>
        {isNewMessageOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] sm:max-h-[85vh] flex flex-col"
            >
              <div className="bg-gray-950 p-6 text-white flex justify-between items-center shrink-0">
                <div>
                  <h3 className="text-xl font-black uppercase italic tracking-tighter">Compose Message</h3>
                  <p className="text-xs text-gray-400 font-medium">Send a message to your team or manager.</p>
                </div>
                <button onClick={() => setIsNewMessageOpen(false)} className="text-gray-500 hover:text-white transition-colors">
                  <Archive size={20} />
                </button>
              </div>
              <form onSubmit={handleSendMessage} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
                {isManager ? (
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Recipient</label>
                    <select 
                      value={newMessage.recipientUid}
                      onChange={e => setNewMessage({...newMessage, recipientUid: e.target.value})}
                      className="input-field w-full"
                      required
                    >
                      <option value="">Select recipient...</option>
                      <option value="broadcast">Everyone (Broadcast Announcement)</option>
                      {staffList
                        .filter(s => s.email !== user?.email)
                        .map(s => (
                          <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
                        ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">To</label>
                    <div className="p-3 bg-gray-50 border border-gray-100 rounded-lg text-sm font-bold text-gray-700 flex items-center gap-2">
                       <UserIcon size={14} />
                       Management Team
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Type</label>
                    <select 
                      value={newMessage.type}
                      onChange={e => setNewMessage({...newMessage, type: e.target.value as any})}
                      className="input-field w-full"
                    >
                      {isManager && <option value="Announcement">Announcement</option>}
                      <option value="Inquiry">General Inquiry</option>
                      <option value="Feedback">Feedback</option>
                      <option value="Support">System Support</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Subject</label>
                  <input 
                    type="text" 
                    value={newMessage.subject}
                    onChange={e => setNewMessage({...newMessage, subject: e.target.value})}
                    className="input-field w-full"
                    placeholder="Brief summary..."
                    required
                  />
                </div>

                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1.5">Message Body</label>
                  <textarea 
                    value={newMessage.body}
                    onChange={e => setNewMessage({...newMessage, body: e.target.value})}
                    className="input-field w-full min-h-[100px] sm:min-h-[150px]"
                    placeholder="Details..."
                    required
                  />
                </div>

                <div className="pt-4 flex gap-3 shrink-0">
                  <button type="button" onClick={() => setIsNewMessageOpen(false)} className="flex-1 px-6 py-3 border border-gray-100 text-gray-500 font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-gray-50 transition-all">
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 btn-primary flex items-center justify-center gap-2">
                    <Send size={16} />
                    Send Message
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
